import { Routes, Route } from "react-router-dom";

import { useEffect, useState, Suspense, lazy } from "react";
import ChatPage from "./components/ChatPage";

import { useSocket } from "./context/SocketProvider";
import Loading from "./components/Loading/Loading";
import LoginPage from "./pages/Authentication/LoginPage";
import SignupPage from "./pages/Authentication/SignupPage";
import { useLogin } from "./context/LoginProvider";
import ProtectedSignup from "./pages/Authentication/ProtectedSignup";
import ForgotPassword from "./pages/Authentication/ForgotPassword";
import { useEmbeddings } from "./context/EmbeddingsProvider";
import Profile from './pages/Profile/Profile';
import FeedbackForm from './components/Feedback/FeedbackForm';
import GuestInfoForm from "./pages/Authentication/GuestInfoForm";
import ProfileSummary from "./components/ProfileSummary/ProfileSummary";

// Lazy load the Homepage component
const Homepage = lazy(() => import("./pages/Homepage"));
// Lazy load legal and information pages
const AboutUs = lazy(() => import("./pages/LegalPages/AboutUs"));
const TermsConditions = lazy(() => import("./pages/LegalPages/TermsConditions"));
const PrivacyPolicy = lazy(() => import("./pages/LegalPages/PrivacyPolicy"));
const ContactUs = lazy(() => import("./pages/LegalPages/ContactUs"));


export default function Home() {
  const { socket, audioSocket } = useSocket();
  const {
    isLoggedIn,
    userData,
    guestData,
    setGuestUserData,
    setUserData,
    logoutUser,
    setLoggedIn,
  } = useLogin();

  const [isJoined, setisJoined] = useState(false);
  const [roomId, setroomId] = useState("");
  const { embeddings, setEmbeddings, isEmbeddingsLoaded, setEmbeddingsLoaded } =
    useEmbeddings();

  const [token, setToken] = useState(null);
  const acquireSession = (userData) => {
    return new Promise((resolve, reject) => {
      if (!socket) {
        reject(new Error("Socket not connected"));
        return;
      }

      socket.emit("acquireSession", userData, (response) => {
        if (response.error) {
          reject(new Error(response.error));
        } else {
          resolve(response.user);
        }
      });
    });
  };
  useEffect(() => {
    async function attemptLogin() {
      const loggedInUser = localStorage.getItem("user");
      try {
        if (loggedInUser) {
          const foundUser = JSON.parse(loggedInUser);
          await acquireSession(foundUser);
          setUserData(foundUser);
          setLoggedIn(true);
        }
      } catch (e) {
        console.log("Cant acquire session" + e);
      }
    }
    attemptLogin();

    // Check for guest data in localStorage first - but only if we don't already have guest data
    // AND only if we're not logged in
    if (!isLoggedIn && guestData.username === "") {
      const storedGuestData = localStorage.getItem("guestUser");
      if (storedGuestData) {
        try {
          const parsedGuestData = JSON.parse(storedGuestData);
          console.log("[Home] Restored guest data from localStorage:", parsedGuestData);
          setGuestUserData(parsedGuestData);
        } catch (e) {
          console.error("Error parsing guest data from localStorage:", e);
          createNewGuestUser();
        }
      } else {
        // Only create a new guest user if we're on the home page and not navigating from login
        // Check if we're coming from a login/signup flow
        const currentPath = window.location.pathname;
        if (currentPath === "/" || currentPath === "/home") {
          createNewGuestUser();
        }
      }
    }
  }, []); // Remove dependencies to prevent re-running

  // Helper function to create a new guest user
  const createNewGuestUser = () => {
    const RandomNumber = Math.floor(100000 + Math.random() * 900000).toString();
    let newUsername = "guest" + Date.now() + RandomNumber;
    const newGuestData = { 
      username: newUsername, 
      name: "",
      age: "",
      country: "",
      interests: [],
      isFreshGuest: true // Flag to indicate this is a fresh guest user
    };
    setGuestUserData(newGuestData);

    // Store in localStorage
    localStorage.setItem("guestUser", JSON.stringify(newGuestData));
    console.log("[Home] Created new guest user:", newUsername);
  };

  useEffect(() => {
    if (embeddings.length > 0) {
      setEmbeddingsLoaded(true);
    }
  }, [embeddings]);

  useEffect(() => {
    if (userData.interests.length !== 0) {
      socket.emit("getEmbeddings", { interests: userData.interests });
    }
  }, [userData]);
  useEffect(() => {
    if (guestData.interests.length !== 0) {
      socket.emit("getEmbeddings", { interests: guestData.interests });
    }
  }, [guestData]);

  const handleForceLogout = () => {
    logoutUser();
  };
  const handlePostEmbeddings = (data) => {
    setEmbeddings(data.embeddings);
  };
  const handlePostInterests = (data) => {
    console.log(`[handlePostInterests] Received updated interests:`, data);
    console.log(`[handlePostInterests] Current login status: isLoggedIn=${isLoggedIn}`);

    // Always update embeddings if provided, regardless of login status
    if (data.embeddings) {
      console.log(`[handlePostInterests] Updating embeddings`);
      setEmbeddings(data.embeddings);
      setEmbeddingsLoaded(true);
    }

    // Check for guest username mismatch and handle it
    if (!isLoggedIn && data.username && data.username.startsWith('guest') && guestData.username === "") {
      console.log(`[handlePostInterests] Setting missing guest username: ${data.username}`);
      const newGuestData = {
        username: data.username,
        interests: data.updatedInterest || [],
      };
      setGuestUserData(newGuestData);
      // Store in localStorage
      localStorage.setItem("guestUser", JSON.stringify(newGuestData));
      return;
    }

    // For existing users, verify the username matches
    const currentUsername = isLoggedIn ? userData.username : guestData.username;
    if (data.username && data.username !== currentUsername && guestData.username !== "") {
      console.log(`[handlePostInterests] Username mismatch: ${data.username} vs ${currentUsername}`);
      return;
    }

    // Update interests based on the current login status
    if (isLoggedIn) {
      console.log(`[handlePostInterests] Updating logged-in user interests`);
      setUserData((prevUserData) => ({
        ...prevUserData,
        interests: data.updatedInterest,
      }));
    } else {
      console.log(`[handlePostInterests] Updating guest interests`);
      setGuestUserData((prevGuestData) => {
        const updatedGuestData = {
        ...prevGuestData,
        interests: data.updatedInterest,
        isFreshGuest: false
        };
        // Save updated guest data to localStorage
        localStorage.setItem("guestUser", JSON.stringify(updatedGuestData));
        return updatedGuestData;
      });
    }

    console.log(`[handlePostInterests] Interests updated successfully`);
  };

  //Hook to set embedding on socket call
  useEffect(() => {
    if (socket === null) return;

    socket.on("forceLogout", handleForceLogout);
    socket.on("postEmbeddings", handlePostEmbeddings);
    socket.on("postInterests", handlePostInterests);
    return () => {
      socket.off("forceLogout", handleForceLogout);
      socket.off("postEmbeddings", handlePostEmbeddings);
      socket.off("postInterests", handlePostInterests);
    };
  }, [socket]);

  useEffect(() => {
    if (audioSocket === null) return;

    audioSocket.on("forceLogout", handleForceLogout);
    audioSocket.on("postEmbeddings", handlePostEmbeddings);
    audioSocket.on("postInterests", handlePostInterests);
    return () => {
      audioSocket.off("forceLogout", handleForceLogout);
      audioSocket.off("postEmbeddings", handlePostEmbeddings);
      audioSocket.off("postInterests", handlePostInterests);
    };
  }, [audioSocket]);
  return (
    <>
      <Suspense fallback={<Loading />}>
        <Routes>
          <Route path="/" element={<Homepage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/cupid" element={<ProtectedSignup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/feedback" element={<FeedbackForm />} />
          <Route path="/guest-info" element={<GuestInfoForm />} />
          <Route path="/profile-summary" element={<ProfileSummary />} />
          <Route
            path="/chat"
            element={
              <ChatPage
                roomId={roomId}
                setroomId={setroomId}
                isJoined={isJoined}
                setisJoined={setisJoined}
              />
            }
          />
          {/* New routes for legal and information pages */}
          <Route path="/about" element={<AboutUs />} />
          <Route path="/terms" element={<TermsConditions />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/contact" element={<ContactUs />} />
        </Routes>
      </Suspense>
    </>
  );
}